import { Link } from '@tanstack/react-router'
import { Clock, ExternalLink } from 'lucide-react'

import type { TeamWorkload } from '@/hooks'

interface TeamWorkloadWidgetProps {
  workload: TeamWorkload[] | null
  isLoading?: boolean
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
      <div
        className="h-full bg-primary rounded-full transition-all"
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  )
}

function WorkloadItem({ item }: { item: TeamWorkload }) {
  const percentage = item.maxHours > 0 ? (item.hoursThisWeek / item.maxHours) * 100 : 0

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{item.employeeName}</span>
          {item.teamName && (
            <span className="text-xs text-muted-foreground">{item.teamName}</span>
          )}
        </div>
        <span className="text-sm font-medium">{item.hoursThisWeek}h</span>
      </div>
      <ProgressBar value={percentage} />
    </div>
  )
}

function WorkloadItemSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="h-4 w-8 bg-muted rounded" />
      </div>
      <div className="h-2 w-full bg-muted rounded" />
    </div>
  )
}

export function TeamWorkloadWidget({ workload, isLoading }: TeamWorkloadWidgetProps) {
  const totalHours = workload?.reduce((sum, item) => sum + item.hoursThisWeek, 0) ?? 0

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between p-6 pb-4">
        <h3 className="flex items-center gap-2 font-semibold">
          <Clock className="h-5 w-5" />
          团队工时
        </h3>
        <Link
          to="/pm/timelogs"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          查看详情 <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <div className="px-6 pb-6 space-y-4">
        {isLoading ? (
          <>
            <WorkloadItemSkeleton />
            <WorkloadItemSkeleton />
            <WorkloadItemSkeleton />
          </>
        ) : !workload || workload.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            本周暂无工时记录
          </div>
        ) : (
          <>
            {workload.map((item) => (
              <WorkloadItem key={item.employeeId} item={item} />
            ))}
            <div className="pt-2 border-t flex items-center justify-between text-sm">
              <span className="text-muted-foreground">本周总工时</span>
              <span className="font-medium">{totalHours}h</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
